import React, { useState, useEffect } from "react";
import {
  VStack,
  Box,
  Heading,
  Text,
  Divider,
  Container,
  Badge,
  useColorModeValue,
  Skeleton,
  SkeletonText,
  Stack,
  useBreakpointValue,
  useTheme,
  Link,
} from "@chakra-ui/react";
import useLocale from "src/providers/useLocale";
import { get } from "src/api";
import { useIntl } from "react-intl";
import { TermsItem, Translation } from "./types";
import DOMPurify from "dompurify";

const TermsAndConditions = () => {
  const [locale] = useLocale();
  const [content, setContent] = useState<TermsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { formatMessage } = useIntl();

  // Consistent styling configurations
  const styles = {
    bg: useColorModeValue("white", "gray.800"),
    border: useColorModeValue("gray.200", "gray.700"),
    spacing: {
      container: useBreakpointValue({ base: 4, sm: 6, md: 8 }),
      content: useBreakpointValue({ base: 3, sm: 4, md: 6 }),
      stack: useBreakpointValue({ base: 2, sm: 3, md: 4 }),
    },
    padding: {
      container: useBreakpointValue({ base: 4, sm: 6, md: 10 }),
      box: useBreakpointValue({ base: 4, sm: 6, md: 8 }),
    },
    fontSize: {
      heading: useBreakpointValue({ base: "md", sm: "md", md: "lg" }),
      subheading: useBreakpointValue({ base: "sm", sm: "sm", md: "md" }),
      body: useBreakpointValue({ base: "sm", sm: "md", md: "md" }),
      badge: useBreakpointValue({ base: "xs", sm: "sm", md: "sm" }),
    },
    fontFamily: "'Calibri', 'Segoe UI', system-ui, sans-serif",
    layout: {
      stackDir: useBreakpointValue({ base: "column", md: "row" }) as
        | "column"
        | "row",
      maxWidth: useBreakpointValue({
        base: "100%",
        sm: "90%",
        md: "container.lg",
      }),
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await get<TermsItem[]>(
          "/terms-and-conditions",
          locale
        );
        setContent(response);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [locale]);

  const getTranslation = (translations: Translation[]) => {
    return translations.find((t) => t.locale === locale) || translations[0];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(
      locale === "ar" ? "ar-u-ca-gregory" : "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  };

  const processHtml = (html: string) => {
    // First clean the HTML with DOMPurify
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "p",
        "ul",
        "ol",
        "li",
        "strong",
        "em",
        "a",
        "br",
      ],
      ALLOWED_ATTR: ["href", "target", "rel"],
    });

    // Process the HTML to remove unwanted whitespace
    return (
      clean
        // Remove whitespace before and after HTML tags
        .replace(/>\s+</g, "><")
        // Replace multiple spaces with single space
        .replace(/\s+/g, " ")
        // Remove spaces before closing tags
        .replace(/\s+>/g, ">")
        // Remove spaces after opening tags
        .replace(/<\s+/g, "<")
        // Replace multiple consecutive br tags with single one
        .replace(/<br\s*\/?>\s*(<br\s*\/?>\s*)+/g, "<br/>")
        // Remove br tags at the start or end of content
        .replace(/^(\s*<br\s*\/?>\s*)+/, "")
        .replace(/(\s*<br\s*\/?>\s*)+$/, "")
        // Remove whitespace at start and end
        .trim()
    );
  };

  const TermsContent = ({ item }: { item: TermsItem }) => {
    const translation = getTranslation(item.translations);
    const processedHtml = processHtml(translation.html);

    return (
      <Box
        bg={styles.bg}
        p={styles.padding.box}
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor={styles.border}
        w="100%"
      >
        <VStack align="stretch" spacing={styles.spacing.content}>
          <Box>
            <Stack
              direction={styles.layout.stackDir}
              spacing={styles.spacing.stack}
              align="flex-start"
              justify="space-between"
              w="100%"
            >
              {/* <Heading
                size={styles.fontSize.subheading as any}
                textStyle="smallBold"
                fontFamily={styles.fontFamily}
                color="#1f3f93"
              >
                {translation.name}
              </Heading> */}

              <Text
                color="gray.500"
                fontSize={styles.fontSize.body}
                fontFamily={styles.fontFamily}
                mt={2}
              >
                {formatMessage({ id: "t&c.lastUpdated" })}:{" "}
                {formatDate(item.updatedAt)}
              </Text>
              <Badge
                colorScheme="blue"
                borderRadius="full"
                px={3}
                py={1}
                fontSize={styles.fontSize.badge}
                fontFamily={styles.fontFamily}
              >
                {formatMessage(
                  { id: "t&c.version" },
                  { version: item.version }
                )}
              </Badge>
            </Stack>
          </Box>

          <Divider />

          <Box
            className="terms-content"
            sx={{
              // Base container styles
              margin: 0,
              padding: 0,
              // Base styles for all content
              "& > *": {
                mb: 2,
                "&:last-child": { mb: 0 },
                "&:empty": { display: "none" },
                fontFamily: styles.fontFamily,
              },
              // Content styles
              "& p": {
                fontSize: styles.fontSize.body,
                lineHeight: "tall",
                margin: 0,
                fontFamily: styles.fontFamily,
                "& + p": { mt: 2 },
              },
              "& ul, & ol": {
                margin: 0,
                pl: { base: 4, sm: 5, md: 6 },
                my: 2,
                "& li": {
                  fontSize: styles.fontSize.body,
                  lineHeight: "tall",
                  mb: 1,
                  "&:last-child": { mb: 0 },
                },
              },
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                fontSize: styles.fontSize.subheading,
                fontWeight: "bold",
                lineHeight: "shorter",
                mt: { base: 4, md: 5 },
                mb: 2,
                fontFamily: styles.fontFamily,
              },
              "& a": {
                color: "blue.500",
                textDecoration: "underline",
                _hover: { color: "blue.600" },
              },
              "& br": {
                content: '""',
                display: "block",
                mb: 1,
                "& + br": { display: "none" },
              },
              // Spacing adjustments
              "& ul + p, & ol + p": { mt: 2 },
              "& p + ul, & p + ol": { mt: 2 },
              "& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p": {
                mt: 2,
              },
              // First and last element adjustments
              "& > *:first-of-type": { mt: 0 },
              "& > *:last-child": { mb: 0 },
              // Clear floats
              "&:after": {
                content: '""',
                display: "table",
                clear: "both",
              },
            }}
            dangerouslySetInnerHTML={{ __html: processedHtml }}
          />
        </VStack>
      </Box>
    );
  };

  const LoadingSkeleton = () => (
    <Box
      bg={styles.bg}
      p={styles.padding.box}
      borderRadius="lg"
      boxShadow="sm"
      border="1px"
      borderColor={styles.border}
      w="100%"
    >
      <VStack align="stretch" spacing={styles.spacing.content}>
        <Stack
          direction={styles.layout.stackDir}
          spacing={styles.spacing.stack}
          align="flex-start"
        >
          <Skeleton height={styles.fontSize.subheading} width="60%" />
          <Skeleton
            height={styles.fontSize.badge}
            width="20%"
            borderRadius="full"
          />
        </Stack>
        <Divider />
        <SkeletonText
          mt={styles.spacing.stack}
          noOfLines={8}
          spacing={styles.spacing.stack}
          skeletonHeight="4"
        />
      </VStack>
    </Box>
  );

  return (
    <Container
      maxW={styles.layout.maxWidth}
      py={styles.padding.container}
      px={styles.padding.box}
    >
      <VStack spacing={styles.spacing.container} align="stretch">
        <Box textAlign="center">
          <Heading
            size={styles.fontSize.heading as any}
            textStyle="smallBold"
            mb={styles.spacing.stack}
            fontFamily={styles.fontFamily}
            color="#1f3f93"
          >
            {formatMessage({ id: "t&c" })}
          </Heading>
        </Box>

        {isLoading
          ? Array.from({ length: 2 }).map((_, index) => (
              <LoadingSkeleton key={`skeleton-${index}`} />
            ))
          : content.map((item) => <TermsContent key={item.id} item={item} />)}

        <Box textAlign="center" pt={styles.spacing.stack}>
          <Text fontSize={styles.fontSize.body} color="gray.500">
            {formatMessage({ id: "t&c.contactUs.prefix" })}{" "}
            <Link
              href="https://t.me/SmartsWay125"
              isExternal
              color="blue.500"
              textDecoration="underline"
              _hover={{ color: "blue.600" }}
            >
              {formatMessage({ id: "t&c.contactUs.link" })}
            </Link>{" "}
            {formatMessage({ id: "t&c.contactUs.suffix" })}
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default TermsAndConditions;
